import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Drawer,
  Descriptions,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/index';
import AdminPermissionModal from '../../components/AdminPermissionModal';
import '../../styles/third-party.css';

const AdminList = () => {
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({});
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [permissionVisible, setPermissionVisible] = useState(false);
  const [permissionAdmin, setPermissionAdmin] = useState(null);

  // 获取管理员列表
  const fetchAdmins = async (page = 1, pageSize = 20, searchFilters = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        ...searchFilters,
      };
      const response = await api.get('/admin/admins', { params });
      
      if (response.code === 200) {
        setAdmins(response.data.list || []);
        setPagination({
          current: page,
          pageSize,
          total: response.data.total || 0,
        });
      } else {
        message.error(response.message || '获取管理员列表失败');
      }
    } catch (error) {
      message.error(error.message || '获取管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins(pagination.current, pagination.pageSize, filters);
  }, []);

  // 搜索处理
  const handleSearch = (values) => {
    setFilters(values);
    setPagination({ current: 1, pageSize: 20, total: 0 });
    fetchAdmins(1, 20, values);
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setPagination({ current: 1, pageSize: 20, total: 0 });
    fetchAdmins(1, 20, {});
  };

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedAdmin(record);
    setDetailVisible(true);
  };

  // 编辑管理员
  const handleEdit = (record) => {
    setEditingAdmin(record);
    editForm.setFieldsValue({
      phone: record.phone,
      real_name: record.real_name,
      email: record.email,
      role: record.role,
      status: record.status,
    });
    setEditVisible(true);
  };

  // 保存编辑
  const handleEditSave = async (values) => {
    try {
      const response = await api.put(`/admin/admins/${editingAdmin.id}`, values);
      
      if (response.code === 200) {
        message.success('更新成功');
        setEditVisible(false);
        setEditingAdmin(null);
        fetchAdmins(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(response.message || '更新失败');
      }
    } catch (error) {
      message.error(error.message || '更新失败');
    }
  };

  // 创建管理员
  const handleCreateSave = async (values) => {
    try {
      const response = await api.post('/admin/admins', values);
      
      if (response.code === 200) {
        message.success('创建成功');
        setCreateVisible(false);
        createForm.resetFields();
        fetchAdmins(1, pagination.pageSize, filters);
      } else {
        message.error(response.message || '创建失败');
      }
    } catch (error) {
      message.error(error.message || '创建失败');
    }
  };

  // 删除管理员
  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/admin/admins/${id}`);
      
      if (response.code === 200) {
        message.success('删除成功');
        fetchAdmins(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  // 修改状态
  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api.put(`/admin/admins/${id}/status`, {
        status: newStatus,
      });
      
      if (response.code === 200) {
        message.success('状态更新成功');
        fetchAdmins(pagination.current, pagination.pageSize, filters);
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error(error.message || '状态更新失败');
    }
  };

  // 分配权限
  const handleAssignPermission = (record) => {
    setPermissionAdmin(record);
    setPermissionVisible(true);
  };

  // 权限分配成功回调
  const handlePermissionSuccess = () => {
    message.success('权限分配成功');
    fetchAdmins(pagination.current, pagination.pageSize, filters);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 88,
      render: (val) => val || <span className="tp-empty">-</span>
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (val) => val || <span className="tp-empty">-</span>
    },
    {
      title: '姓名',
      dataIndex: 'real_name',
      key: 'real_name',
      width: 120,
      render: (val) => val ? <span className="tp-ellipsis" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (email) => email ? <span className="tp-ellipsis" title={email}>{email}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => {
        const roleConfig = {
          super_admin: { tone: 'failed', text: '超级管理员' },
          admin: { tone: 'processing', text: '普通管理员' },
          operator: { tone: 'pending', text: '操作员' },
        };
        const config = roleConfig[role] || { tone: 'muted', text: role };
        return <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 88,
      render: (status) => {
        const statusConfig = {
          0: { tone: 'failed', text: '禁用' },
          1: { tone: 'paid', text: '正常' },
          2: { tone: 'processing', text: '冻结' },
        };
        const config = statusConfig[status] || { tone: 'muted', text: status };
        return <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>;
      },
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_time',
      key: 'last_login_time',
      width: 160,
      render: (date) => date ? <span className="tp-time">{formatDateTime(date, 'YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date) => date ? <span className="tp-time">{formatDateTime(date, 'YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 168,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-icon-actions">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="分配权限">
            <Button
              type="text"
              size="small"
              icon={<SafetyOutlined />}
              onClick={() => handleAssignPermission(record)}
            />
          </Tooltip>
          {record.status === 1 ? (
            <Tooltip title="禁用">
              <Popconfirm
                title="禁用管理员"
                description="确定要禁用该管理员吗？"
                onConfirm={() => handleStatusChange(record.id, 0)}
              >
                <Button type="text" size="small" icon={<LockOutlined />} danger />
              </Popconfirm>
            </Tooltip>
          ) : (
            <Tooltip title="启用">
              <Popconfirm
                title="启用管理员"
                description="确定要启用该管理员吗？"
                onConfirm={() => handleStatusChange(record.id, 1)}
              >
                <Button type="text" size="small" icon={<UnlockOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title="删除">
            <Popconfirm
              title="删除管理员"
              description="确定要删除该管理员吗？此操作不可撤销。"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar">
        <Form form={form} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="keyword">
            <Input
              placeholder="手机号或姓名"
              prefix={<SearchOutlined />}
              className="tp-field--lg"
              allowClear
            />
          </Form.Item>
          <Form.Item name="role">
            <Select
              placeholder="角色"
              className="tp-field--md"
              allowClear
            >
              <Select.Option value="super_admin">超级管理员</Select.Option>
              <Select.Option value="admin">普通管理员</Select.Option>
              <Select.Option value="operator">操作员</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="状态"
              className="tp-field--sm"
              allowClear
            >
              <Select.Option value={0}>禁用</Select.Option>
              <Select.Option value={1}>正常</Select.Option>
              <Select.Option value={2}>冻结</Select.Option>
            </Select>
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
              创建管理员
            </Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-table-card">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={admins}
            rowKey="id"
            size="middle"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (page, pageSize) => {
                fetchAdmins(page, pageSize, filters);
              },
            }}
            scroll={{ x: 1280 }}
          />
        </Spin>
      </div>

      {/* 详情抽屉 */}
      <Drawer
        title="管理员详情"
        placement="right"
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
        width={500}
      >
        {selectedAdmin && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{selectedAdmin.id}</Descriptions.Item>
            <Descriptions.Item label="手机号">{selectedAdmin.phone}</Descriptions.Item>
            <Descriptions.Item label="姓名">{selectedAdmin.real_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{selectedAdmin.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="角色">
              {
                {
                  super_admin: '超级管理员',
                  admin: '普通管理员',
                  operator: '操作员',
                }[selectedAdmin.role] || selectedAdmin.role
              }
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <span className={`tp-tag tp-tag--${{ 0: 'failed', 1: 'paid', 2: 'processing' }[selectedAdmin.status] || 'muted'}`}>
                {{ 0: '禁用', 1: '正常', 2: '冻结' }[selectedAdmin.status] || selectedAdmin.status}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {selectedAdmin.last_login_time ? formatDateTime(selectedAdmin.last_login_time) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDateTime(selectedAdmin.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {formatDateTime(selectedAdmin.updated_at)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* 创建管理员Modal */}
      <Modal
        className="tp-modal"
        title="创建管理员"
        open={createVisible}
        onCancel={() => {
          setCreateVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSave}
        >
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>
          <Form.Item
            name="real_name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ type: 'email', message: '邮箱格式不正确' }]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Select.Option value="super_admin">超级管理员</Select.Option>
              <Select.Option value="admin">普通管理员</Select.Option>
              <Select.Option value="operator">操作员</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑管理员Modal */}
      <Modal
        className="tp-modal"
        title="编辑管理员"
        open={editVisible}
        onCancel={() => {
          setEditVisible(false);
          setEditingAdmin(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSave}
        >
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input placeholder="请输入手机号" disabled />
          </Form.Item>
          <Form.Item
            name="real_name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ type: 'email', message: '邮箱格式不正确' }]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Select.Option value="super_admin">超级管理员</Select.Option>
              <Select.Option value="admin">普通管理员</Select.Option>
              <Select.Option value="operator">操作员</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Select.Option value={0}>禁用</Select.Option>
              <Select.Option value={1}>正常</Select.Option>
              <Select.Option value={2}>冻结</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限分配Modal */}
      {permissionAdmin && (
        <AdminPermissionModal
          visible={permissionVisible}
          adminId={permissionAdmin.id}
          adminName={permissionAdmin.real_name}
          onClose={() => {
            setPermissionVisible(false);
            setPermissionAdmin(null);
          }}
          onSuccess={handlePermissionSuccess}
        />
      )}
    </div>
  );
};

export default AdminList;
